import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { enumTypeView } from '@enum/enumTypeView';
import { CompaniesService } from '@services/companies.service';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { enumCompanyBusinessType } from '@app/data-layer/enums/companies';
import { enumIdentifierQualifier } from '@app/data-layer/enums/common';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { isBannedIorAddress } from '@helpers/banned-address.helper';

@Component({
  selector: 'company-address-form',
  templateUrl: './company-address-form.component.html',
  styleUrls: ['./company-address-form.component.scss'],
  // TODO: comment for resolving issue with showing validation borders to inputs
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyAddressFormComponent implements OnInit, OnDestroy {
  // #13189 — controls painted red on a banned-address match; line3 stays clean per design.
  private static readonly BANNED_ADDRESS_CONTROLS: string[] = ['line1', 'line2', 'city', 'country', 'state', 'postalCode'];

  @Input() public formGroup: UntypedFormGroup;
  @Input() public isShowDescription: boolean = false;
  @Input() public isDisableCountry: boolean = true;

  public enumCompanyBusinessType: typeof enumCompanyBusinessType = enumCompanyBusinessType;
  public enumIdentifierQualifier: typeof enumIdentifierQualifier = enumIdentifierQualifier;
  public enumTypeView: typeof enumTypeView = enumTypeView;
  public errors: { [name: string]: IErrorForm } = {
    city: { required: 'FORM_ERROR.REQUIRED' },
    state: { required: 'FORM_ERROR.REQUIRED' },
    postalCode: { required: 'FORM_ERROR.REQUIRED' },
  };
  public defaultCountry: string = 'US';
  public isBannedAddress: boolean = false;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject();

  constructor(private companiesService: CompaniesService, private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.companiesService.companyUpdated$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());

    this.formGroup
      .get('identifierQualifier')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cdr.markForCheck());

    const addressGroup: UntypedFormGroup | null = this.formGroup.get('address') as UntypedFormGroup | null;
    if (addressGroup !== null) {
      addressGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.recomputeBannedAddress());
      // Edit flow: the company may load with a banned address already saved — highlight immediately.
      this.recomputeBannedAddress();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public get isForeignImporter(): boolean {
    return this.formGroup.get('identifierQualifier')?.value === enumIdentifierQualifier.ANI;
  }

  public get isUnitedStates(): boolean {
    return this.formGroup.get('isUnitedStates')?.value;
  }

  // #13189 — red borders WITHOUT per-field error text: `bannedAddress` is set as a control
  // error (control wrappers paint the border), but the key is deliberately absent from every
  // `errors` map — a single section-wide message renders below the address form instead.
  private recomputeBannedAddress(): void {
    const addressGroup: UntypedFormGroup | null = this.formGroup.get('address') as UntypedFormGroup | null;
    if (addressGroup === null) {
      return;
    }

    const banned: boolean = isBannedIorAddress(addressGroup.getRawValue());
    this.isBannedAddress = banned;
    CompanyAddressFormComponent.BANNED_ADDRESS_CONTROLS.forEach((name: string) => {
      const control: AbstractControl | null = addressGroup.get(name);
      if (control === null) {
        return;
      }
      this.setBannedError(control, banned);
      if (banned) {
        // onlySelf: show red live/on-load without dirtying the parent (mirrors #13181).
        control.markAsTouched({ onlySelf: true });
      }
    });
    this.cdr.markForCheck();
  }

  private setBannedError(control: AbstractControl, banned: boolean): void {
    if (banned === control.hasError('bannedAddress')) {
      return;
    }

    const currentErrors: ValidationErrors = control.errors === null ? {} : control.errors;
    if (banned) {
      control.setErrors({ ...currentErrors, bannedAddress: true });
      return;
    }

    const remaining: ValidationErrors = {};
    Object.keys(currentErrors).forEach((key: string) => {
      if (key !== 'bannedAddress') {
        remaining[key] = currentErrors[key];
      }
    });
    control.setErrors(Object.keys(remaining).length > 0 ? remaining : null);
  }
}
